/**
 * Cosmic Lottery — weapon selection slot machine.
 *
 * Deterministic RNG per lottery roll (seeded from terrain seed + turn count).
 * 3 independent rarity rolls → weapon picks. Pity counter guarantees Rare+
 * after PITY_THRESHOLD dry turns.
 */

import { SeededRNG } from '../terrain/rng';
import { LOTTERY } from '../constants';
import type { Rarity, WeaponDef } from '../weapons';
import { getWeaponsByRarity, MORTAR } from '../weapons';
import type { GameState } from '../types';

/** Rarity tiers in order, with cumulative weights for weighted roll. */
const RARITY_TABLE: { rarity: Rarity; weight: number }[] = [
  { rarity: 'common', weight: LOTTERY.WEIGHT_COMMON },
  { rarity: 'uncommon', weight: LOTTERY.WEIGHT_UNCOMMON },
  { rarity: 'rare', weight: LOTTERY.WEIGHT_RARE },
  { rarity: 'epic', weight: LOTTERY.WEIGHT_EPIC },
  { rarity: 'legendary', weight: LOTTERY.WEIGHT_LEGENDARY },
];

const TOTAL_WEIGHT = RARITY_TABLE.reduce((sum, r) => sum + r.weight, 0);

function isRarePlus(rarity: Rarity): boolean {
  return rarity === 'rare' || rarity === 'epic' || rarity === 'legendary';
}

/** Create a deterministic RNG for this specific lottery roll. */
function getLotteryRNG(terrainSeed: number, turnCount: number): SeededRNG {
  return new SeededRNG(terrainSeed ^ (turnCount * 0x9E3779B9));
}

/** Roll a rarity tier using weighted random selection. */
function rollRarity(rng: SeededRNG): Rarity {
  let roll = rng.next() * TOTAL_WEIGHT;
  for (const entry of RARITY_TABLE) {
    roll -= entry.weight;
    if (roll <= 0) return entry.rarity;
  }
  return 'common'; // fallback
}

/** Pick a random weapon from a rarity tier, excluding already-picked weapons. */
function pickWeaponFromTier(rarity: Rarity, rng: SeededRNG, exclude: Set<string>): WeaponDef {
  let pool = getWeaponsByRarity(rarity).filter(w => !exclude.has(w.id));
  if (pool.length === 0) {
    // Fallback: try full tier (ignore exclude), then common
    pool = getWeaponsByRarity(rarity);
    if (pool.length === 0) {
      pool = getWeaponsByRarity('common').filter(w => !exclude.has(w.id));
      if (pool.length === 0) pool = getWeaponsByRarity('common');
    }
  }
  return rng.pick(pool);
}

/**
 * Roll 3 lottery slots for the current player.
 * Updates the player's pity counter.
 * Returns 3 WeaponDefs (one per slot).
 */
export function rollLottery(state: GameState): WeaponDef[] {
  const rng = getLotteryRNG(state.terrainSeed, state.turnCount);
  const player = state.players[state.currentPlayer];
  const results: WeaponDef[] = [];

  let hasRarePlus = false;
  const picked = new Set<string>();

  for (let i = 0; i < 3; i++) {
    let rarity = rollRarity(rng);

    // Pity: force last slot to rare if counter >= threshold and no rare+ yet
    if (i === 2 && !hasRarePlus && player.pityCounter >= LOTTERY.PITY_THRESHOLD) {
      rarity = 'rare';
    }

    if (isRarePlus(rarity)) {
      hasRarePlus = true;
    }

    const weapon = pickWeaponFromTier(rarity, rng, picked);
    picked.add(weapon.id);
    results.push(weapon);
  }

  // 4th slot: always mortar
  results.push(MORTAR);

  // Update pity counter
  player.pityCounter = hasRarePlus ? 0 : player.pityCounter + 1;

  return results;
}
